import { getCurrencyAffix } from '@app/logic/price';
import { paths } from '@app/paths';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { HttpStatusCode } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LookbookFiltersPanel } from '../products/FiltersBar/FiltersPanel';
import { LookbookProductsList } from '../products/LookbookProductsList';
import { NoResults } from '../products/NoResults';
import { LookbookProductDetailsModal } from '../products/ProductDetails/LookbookProductDetailsModal';
import { ProductsListingHeading } from '../products/ProductsListingHeading';
import { SomethingWentWrong } from '../somethingWentWrong';
import { Button } from '../various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '../various/Dropdown';
import { Currency } from '../various/Icons';

import { useLookbookContext } from './context/LookbookContext';
import { LookbookDetailsModalContextProvider } from './context/LookbookDetailsModalContext';
import { useLookbookProductsContext } from './context/LookbookProductsContext';
import { LookbookProductsGridSwitch } from './LookbookProductsGridSwitch';
interface Props {
  productId: string | undefined;
  variantId: string | undefined;
}
export const LookbookProductsPageContent = ({ variantId, productId }: Props) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = React.useState(false);
  const { lookbookId, pricelists, selectedPricelist, setSelectedPricelistId } = useLookbookContext();
  const {
    fetchNextPage,
    hasNextPage,
    productAttributes,
    sortedProducts,
    error,
    isLoading,
    isLoadingFilters,
    isError,
    filters,
    handleFilterChange,
    isLoadingNextPage,
  } = useLookbookProductsContext();

  React.useEffect(() => {
    if (isDefined(error) && error.response?.status === HttpStatusCode.NotFound) {
      navigate(paths.PAGE_NOT_FOUND, { replace: true });
    }
  }, [error, navigate]);

  const pricelistOptions = React.useMemo(() => {
    return pricelists.map(({ id, currency }) => {
      const currencyAffix = getCurrencyAffix(currency);
      const description = currencyAffix === currency.name ? undefined : currencyAffix;

      return { description, key: id, label: currency.name };
    });
  }, [pricelists]);

  const selectedPricelistLabel = React.useMemo(() => {
    if (!isDefined(selectedPricelist)) {
      return '';
    }

    const { currency } = selectedPricelist;
    const currencyAffix = getCurrencyAffix(currency);

    if (currency.currency === currencyAffix) {
      return currencyAffix;
    }

    return `${currency.currency} (${currencyAffix})`;
  }, [selectedPricelist]);

  const hasProducts = !isEmpty(sortedProducts);
  const shouldShowNoResults = !hasProducts && !isLoading;

  if (isError) {
    return <SomethingWentWrong />;
  }

  return (
    <>
      <div className="relative flex-1 sm:ml-6 lg:ml-[18rem]">
        <LookbookFiltersPanel isDrawerOpen={isFiltersDrawerOpen} onDrawerClose={() => setIsFiltersDrawerOpen(false)} />
        <div>
          <div className="sticky top-[calc(var(--headerWithBannerHeight)+var(--breadcrumbsHeight)+1.5rem)] z-topBar w-full bg-neutral-96 pr-4 transition-[top] group-[.scrollingDown]/body:top-[calc(var(--breadcrumbsHeight)+1.5rem)] sm:top-[calc(var(--headerWithBannerHeight)+var(--breadcrumbsHeight)+1rem)] sm:group-[.scrollingDown]/body:top-[calc(var(--breadcrumbsHeight)+1rem)] lg:top-[calc(var(--headerWithBannerHeightOnDesktop)+var(--breadcrumbsHeight)+1rem)] lg:pr-8">
            <ProductsListingHeading
              isLoading={isLoadingFilters}
              onFiltersOpen={() => setIsFiltersDrawerOpen(true)}
              filters={filters}
              attributes={productAttributes}
              onFilterChange={handleFilterChange}
            >
              <div className="flex justify-between gap-2">
                {pricelists.length > 1 && isDefined(selectedPricelist) && (
                  <Dropdown title={t('common:currency_one')} placement="bottom-end">
                    <DropdownTrigger>
                      {({ isOpen, triggerProps }) => (
                        <Button
                          size="medium"
                          variant="bordered"
                          color="secondary"
                          className="px-4"
                          icon={Currency}
                          isChevronOpen={isOpen}
                          {...triggerProps}
                        >
                          {selectedPricelistLabel}
                        </Button>
                      )}
                    </DropdownTrigger>
                    <DropdownMenu
                      selectedKeys={[selectedPricelist.id]}
                      selectionMode="single"
                      onSelectionChange={([pricelistId]) => setSelectedPricelistId(pricelistId as string)}
                      items={pricelistOptions}
                    />
                  </Dropdown>
                )}
                <div className="hidden xxs:flex">
                  <LookbookProductsGridSwitch />
                </div>
              </div>
            </ProductsListingHeading>
          </div>
          {shouldShowNoResults ?
            <NoResults filters={filters} onFilterChange={handleFilterChange} />
          : <LookbookProductsList />}
        </div>
      </div>
      <LookbookDetailsModalContextProvider
        lookbookId={lookbookId}
        productId={productId}
        variantId={variantId}
        fetchNextPage={fetchNextPage}
        isLoadingMoreProducts={isLoadingNextPage}
        hasNextPage={hasNextPage}
        products={sortedProducts}
      >
        <LookbookProductDetailsModal />
      </LookbookDetailsModalContextProvider>
    </>
  );
};
